import type { ReactNode } from "react";
import { useEffect, useRef } from "react";
import type { TClientLinkContext } from "../Layouts";

export interface EntryListItemProps {
  title: string;
  href: string;
  isActive?: boolean;
  scrollToMe?: boolean;
  description?: string | ReactNode;
  byline?: string | ReactNode;
  CustomLink?: TClientLinkContext;
}

export const EntryListItem = ({
  title,
  href,
  description,
  byline,
  isActive = false,
  scrollToMe = false,
  CustomLink = null,
}: EntryListItemProps) => {
  // Scroll list item to view if needed.
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    if (isActive && scrollToMe && ref?.current) {
      ref.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [isActive, scrollToMe]);
  //////

  const INNER_X_CSS = "px-2";
  const FONT_CSS = "lg:text-sm font-semibold tracking-[-0.01em] lg:tracking-[-0.006em]";

  return (
    <article
      ref={ref}
      className={`${
        isActive
          ? "bg-e-color-primaryContainer text-e-color-onPrimaryContainer"
          : "text-e-color-onSurface hover:bg-e-color-primary/5 focus-within:bg-e-color-primary/5"
      } relative rounded transition-colors duration-75 scroll-mt-16`}
    >
      <div
        className={`${INNER_X_CSS} flex space-x-3 py-4 border-b border-e-color-outlineVariant lg:border-none`}
      >
        <div className="flex flex-col justify-center space-y-1.5">
          {/* 
            // TODO (later) Experiment with font size & letter spacing
            @link https://rsms.me/inter/dynmetrics 
          */}
          <div className={`${FONT_CSS} line-clamp-3`}>
            {!!CustomLink ? (
              <CustomLink href={href}>
                <a data-client-link={1} href={href}>
                  {title}
                </a>
              </CustomLink>
            ) : (
              <a href={href}>{title}</a>
            )}
          </div>
          {description && <div className={`line-clamp-2`}>{description}</div>}
          {byline && <div className={`line-clamp-1 text-xs opacity-75`}>{byline}</div>}
        </div>
      </div>
    </article>
  );
};
